import type { OrderMailboxRepository } from "../../../domain/repositories/company/order-mailbox-repository";
import { orderMailboxApiService } from "../../api/company/order-mailbox-api-service";
import type {
  OrderMailboxListResponse,
  CollectedEmailDetail,
  CollectedEmail,
  CreateOrderFromEmailRequest,
  EstimateItemInput,
} from "../../../domain/types/order-mailbox";

export class OrderMailboxRepositoryImpl implements OrderMailboxRepository {
  async list(
    companyId: number,
    status?: string,
    keyword?: string,
    page?: number,
    size?: number,
  ): Promise<OrderMailboxListResponse> {
    const response = await orderMailboxApiService.list(
      companyId,
      status,
      keyword,
      page,
      size,
    );

    if (!response.data) {
      throw new Error(
        response.errorMsg || "주문 메일 목록을 불러오지 못했습니다.",
      );
    }

    return response.data;
  }

  async detail(companyId: number, id: number): Promise<CollectedEmailDetail> {
    const response = await orderMailboxApiService.detail(companyId, id);

    if (!response.data) {
      throw new Error(
        response.errorMsg || "주문 메일 상세를 불러오지 못했습니다.",
      );
    }

    return response.data;
  }

  async refresh(companyId: number): Promise<number> {
    const response = await orderMailboxApiService.refresh(companyId);
    return response.data ?? 0;
  }

  async createOrder(
    companyId: number,
    id: number,
    request: CreateOrderFromEmailRequest,
  ): Promise<CollectedEmail> {
    const response = await orderMailboxApiService.createOrder(
      companyId,
      id,
      request,
    );

    if (!response.data) {
      throw new Error(response.errorMsg || "주문 생성에 실패했습니다.");
    }

    return response.data;
  }

  async matchCustomer(
    companyId: number,
    id: number,
    userId: number,
  ): Promise<CollectedEmail> {
    const response = await orderMailboxApiService.matchCustomer(
      companyId,
      id,
      userId,
    );

    if (!response.data) {
      throw new Error(response.errorMsg || "고객 연결에 실패했습니다.");
    }

    return response.data;
  }

  async downloadEstimate(
    companyId: number,
    id: number,
    items: EstimateItemInput[],
  ): Promise<{ blob: Blob; fileName: string | null }> {
    return orderMailboxApiService.downloadEstimate(companyId, id, items);
  }

  async sendEstimate(
    companyId: number,
    id: number,
    items: EstimateItemInput[],
    toEmail?: string | null,
  ): Promise<string> {
    const response = await orderMailboxApiService.sendEstimate(
      companyId,
      id,
      items,
      toEmail,
    );

    if (!response.data) {
      throw new Error(response.errorMsg || "견적서 메일 전송에 실패했습니다.");
    }

    return response.data;
  }
}
